import { ref, onUnmounted, computed, watch } from 'vue';
import { MdTypography, MdModal, MdButton, MdIconLoader } from '@antiqua-dls/vue-components';
import { MIN_ORDER_MID_LENGTH, REGEX_COLLECTIONS, INGESTION_TYPES, INGESTION_MODAL_VALIDATIONS, CUSTOM_FOUNDRY_NAME } from 'src/utils/constants/common'
import TextField from 'src/components/form/new-text-field/index.vue';
import FouDropdown from 'components/form/fou-dropdown/';
import { FouSelectObject } from 'src/utils/objects/fou-select';
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { getEnvMetadata, disableScrollOnModalOpen } from 'src/utils/helper/misc';
import { Toaster } from 'src/utils/helper/toaster';
import i18n from 'src/assets/i18n/i18n'
import { isEmpty } from 'lodash';

const htmlDecoder = document.createElement('textarea');
const { t } = i18n().global

const {
  EMOJI_REGEX,
  HTML_SYMBOLS_REGEX,
  SCRIPT_TAG_REGEX,
  SAFE_HTML_TAG_NAME_REGEX,
  SAFE_TAG_ATTR_REGEX,
  SAFE_ENCODED_HTML_TAG_REGEX,
  SAFE_ENCODED_TAG_ATTR_REGEX,
  ENCODED_ANGLE_BRACKETS_REGEX
} = REGEX_COLLECTIONS;

export default {
  name: 'ProjectFormModal',
  components: {
    MdModal,
    TextField,
    FouDropdown,
    MdTypography,
    MdButton,
    MdIconLoader
  },
  props: {
    // Modal Configuration
    triggerModal: {
      type: Boolean,
      default: false
    },
    modalId: {
      type: String,
      default: 'create-project-modal'
    },
    headerText: {
      type: String,
      default: 'Create project'
    },
    editHeaderText: {
      type: String,
      default: 'Edit project'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    proceedText: {
      type: String,
      default: 'Proceed'
    },
    isEditing: {
      type: Boolean,
      default: false
    },

    // Project Type Configuration
    projectTypeProps: {
      type: Object,
      default: {
        showProjectType: true,
        projectTypeOptions: [],
        projectTypePlaceholder: t('pages.dropShip.project_type_placeholder'),
        isProjectTypeDisabled: false
      }
    },
    // Project Name Configuration
    projectNameProps: {
      type: Object,
      default: {
        projectNamePlaceholder: t('pages.dropShip.project_name'),
        projectNameMaxLength: 100,
        projectNameMinLength: 3,
      }
    },

    // Additional Field Configuration (for MID/Order number or other fields)
    additionalFieldProps: {
      type: Object,
      default: {
        showAdditionalField: false,
        additionalFieldName: 'mid number',
        additionalFieldPlaceholder: '',
        additionalFieldMaxLength: 8,
        additionalFieldOptionalText: 'Optional',
        additionalFieldLabel: ''
      }
    },

    // Foundry Configuration
    foundryProps: {
      type: Object,
      default: {
        foundryPlaceholder: t('pages.dropShip.target_foundry'),
        isFoundryDisabled: false
      }
    },


    // Pre-filled Data
    prefilledData: {
      type: Object,
      default: () => ({})
    },

    // Event Handlers
    onSubmit: {
      type: Function,
      required: true
    }
  },
  emits: ['on-cancel'],
  setup(props, { emit }) {
    const customFoundryId = getEnvMetadata()?.customFoundryId;
    const customFoundry = {
      id: customFoundryId,
      name: CUSTOM_FOUNDRY_NAME,
      label: CUSTOM_FOUNDRY_NAME,
      value: CUSTOM_FOUNDRY_NAME
    };
    const customType = INGESTION_TYPES.custom;

    const foundryOptions = ref([]);
    const store = useStore();
    const { validate } = useForm();

    // Destructure grouped props
    const {
      showProjectType = true,
      projectTypeOptions,
      projectTypePlaceholder,
      isProjectTypeDisabled
    } = props.projectTypeProps || {};

    const {
      projectNamePlaceholder,
      projectNameMaxLength,
      projectNameMinLength
    } = props.projectNameProps || {};

    const {
      showAdditionalField,
      additionalFieldName,
      additionalFieldPlaceholder,
      additionalFieldMaxLength,
      additionalFieldOptionalText,
      additionalFieldLabel
    } = props.additionalFieldProps || {};

    const {
      foundryPlaceholder,
      isFoundryDisabled
    } = props.foundryProps || {};

    // Form State
    const projectType = ref('');
    const foundry = ref('');
    const projectName = ref('');
    const additionalFieldValue = ref('');
    const smallPlaceholderPN = ref(false);
    const smallPlaceholderAF = ref(false);
    const showExistingNameError = ref(false);
    const checkForError = ref(false);
    const foundries = FouSelectObject();
    const isOptionLoading = ref(false);
    const proceedBtnLoader = ref(false);

    const computedFoundryOptions = computed(() => {
      return projectType.value === customType ? [customFoundry] : foundryOptions.value;
    })
    const computedFoundryValue = computed(() => {
      return projectType.value === customType ? customFoundry.value : foundry.value
    })

    // Project Name Validation
    const projectNameValidationRule = computed(() => {
      return showExistingNameError.value ? 'existing_mid_error' : 'validate_project_name';
    });

    const midValidationRule = computed(() => {
      return showExistingNameError.value && additionalFieldValue.value
        ? INGESTION_MODAL_VALIDATIONS.existing_mid_error
        : validationErrorRule(projectType.value);
    });

    const validationErrorRule = (ingestionType) => {
      return ingestionType === INGESTION_TYPES.custom ? INGESTION_MODAL_VALIDATIONS.required_only_numeric_mid : INGESTION_MODAL_VALIDATIONS.only_numeric_mid;
    }

    // HTML Entity Decoder for project name
    function decodeHTMLEntities(str) {
      htmlDecoder.innerHTML = str;
      return htmlDecoder.value;
    }

    const onChangePN = (event) => {
      const decoded = decodeHTMLEntities(event.target.value);
      if (decoded !== event.target.value) {
        projectName.value = decoded;
      }
    };

    // Focus/Blur Handlers for Project Name
    const onFocusPN = () => {
      smallPlaceholderPN.value = true;
    };

    const onBlurPN = () => {
      smallPlaceholderPN.value = projectName.value !== '';
      showExistingNameError.value = false;
    };

    // Focus/Blur Handlers for Additional Field
    const onFocusAF = () => {
      smallPlaceholderAF.value = true;
    };

    const onBlurAF = () => {
      smallPlaceholderAF.value = additionalFieldValue.value !== '';
    };

    const onlyNumericMidRule = (mid) => {
      if (mid.length === 0) return true;
      if (mid.length < MIN_ORDER_MID_LENGTH) return false;
      return /^\d+$/.test(mid);
    };

    // Project Name Validation Function (reusing from IH)
    const projectNameRule = (name) => {
      // Block emojis
      if (EMOJI_REGEX.test(name)) return false;
      // Block script tags (encoded and raw)
      if (SCRIPT_TAG_REGEX.test(name)) return false;
      // Block general HTML tags (encoded and raw)
      if (
        SAFE_HTML_TAG_NAME_REGEX.test(name) ||
        SAFE_TAG_ATTR_REGEX.test(name) ||
        SAFE_ENCODED_HTML_TAG_REGEX.test(name) ||
        HTML_SYMBOLS_REGEX.test(name) ||
        SAFE_ENCODED_TAG_ATTR_REGEX.test(name) ||
        ENCODED_ANGLE_BRACKETS_REGEX.test(name)
      ) return false;
      // Must meet minimum length requirement
      return name.length >= projectNameMinLength;
    };



    // Form Validation
    const isFormValid = computed(() => {
      const basicValidation = Boolean(
        (!showProjectType || projectType.value) &&
        foundry.value &&
        !proceedBtnLoader.value &&
        projectNameRule(projectName.value.trim()) &&
        !showExistingNameError.value
      );

      // Additional field validation if required
      if (showAdditionalField) {
        return basicValidation && onlyNumericMidRule(additionalFieldValue.value)
      }
      return basicValidation;
    });

    // Event Handlers
    const handleProjectTypeSelectAction = (option) => {
      projectType.value = option.value;
      if (projectType.value === customType) {
        foundry.value = customFoundry.id;
      }
    };

    const handleFoundrySelectAction = (option) => {
      foundry.value = option.value;
    };

    const updateCheckForError = (val) => {
      checkForError.value = val;
    };

    // Foundry Search
    const handleSearchAction = (searchText) => {
      if (searchText.length > 2) getFoundries(searchText);
    };

    const getFoundries = (searchText) => {
      isOptionLoading.value = true;
      store.dispatch('foundrySupport/getAllFoundries', { foundryName: searchText })
        .then((res) => {
          foundries.setOptions({
            obj: res,
            value: 'id',
            name: 'name',
            multiple: true
          });
          foundryOptions.value = foundries.options;
        })
        .catch(() => {
          // Handle error silently or show toast
        })
        .finally(() => {
          isOptionLoading.value = false;
        });
    };

    // Modal Actions
    const openModal = () => {
      if (props.prefilledData) {
        preFillForm(props.prefilledData);
      }

      // Z-index fix for specific use cases
      if (props.modalId === 'create-edit-modal') {
        setTimeout(() => {
          const modalElement = document.getElementById(props.modalId);
          const referenceElement = document.getElementById('sis-ingestion-dropzone');
          if (referenceElement && modalElement) {
            referenceElement?.parentNode?.insertBefore(modalElement, referenceElement.nextSibling);
          }
        }, 0);
      }
    };

    const closeModal = (shouldEmitCjaEvent) => {
      resetForm();
      proceedBtnLoader.value = false;
      disableScrollOnModalOpen(false); // Ensure body scroll is restored
      emit('on-cancel', shouldEmitCjaEvent)
    };

    const resetForm = () => {
      if (props.isEditing && !isEmpty(props.prefilledData)) {
        return;
      }
      projectType.value = '';
      foundry.value = '';
      projectName.value = '';
      additionalFieldValue.value = '';
      smallPlaceholderPN.value = false;
      smallPlaceholderAF.value = false;
      showExistingNameError.value = false;
      foundryOptions.value = [];
    };

    const preFillForm = (data) => {
      projectType.value = data.projectType || '';
      foundry.value = data.foundryId || '';
      projectName.value = data.projectName || '';
      additionalFieldValue.value = data.additionalFieldValue || '';
      smallPlaceholderPN.value = projectName.value !== '';
      smallPlaceholderAF.value = additionalFieldValue.value !== '';

      if (data.foundryName && data.foundryId) {
        foundryOptions.value = [{
          label: data.foundryName,
          value: data.foundryId
        }];
      }
    };

    const proceedCreation = async () => {
      await validate();
      proceedBtnLoader.value = true;

      const formData = {
        projectType: projectType.value,
        projectName: projectName.value.trim(),
        foundryId: foundry.value,
        additionalFieldValue: additionalFieldValue.value
      };

      try {
        const response = await props.onSubmit(formData);
        if (!response.success && response.error.status === 409) {
          showExistingNameError.value = true;
          proceedBtnLoader.value = false;
          return;
        }
        closeModal(false);
      } catch (error) {
        console.log('error', error)
        proceedBtnLoader.value = false;
        // Handle common error cases
        if (error?.response?.status === 409) {
          showExistingNameError.value = true;
        }
        if (error?.response?.status === 400) {
          Toaster.notifyError(error?.response?.data?.error);
        }
      }
    };


    onUnmounted(() => {
      // Ensure body scroll is restored when component unmounts
      disableScrollOnModalOpen(false);
    });

    watch(() => props.triggerModal, (newValue, oldValue) => {
      // Only call disableScrollOnModalOpen if the value actually changed
      if (newValue !== oldValue) {
        disableScrollOnModalOpen(newValue);
      }

      if (props.prefilledData && newValue) {
        openModal()
      }
    });

    return {
      projectType,
      foundry,
      projectName,
      additionalFieldValue,
      smallPlaceholderPN,
      smallPlaceholderAF,
      showExistingNameError,
      checkForError,
      foundryOptions,
      isOptionLoading,
      proceedBtnLoader,
      projectNameValidationRule,
      isFormValid,
      midValidationRule,
      validationErrorRule,
      preFillForm,
      handleProjectTypeSelectAction,
      handleFoundrySelectAction,
      updateCheckForError,
      handleSearchAction,
      onFocusPN,
      onBlurPN,
      onFocusAF,
      onBlurAF,
      onChangePN,
      openModal,
      closeModal,
      proceedCreation,
      // Destructured props
      showProjectType,
      projectTypeOptions,
      projectTypePlaceholder,
      isProjectTypeDisabled,
      projectNamePlaceholder,
      projectNameMaxLength,
      projectNameMinLength,
      showAdditionalField,
      additionalFieldName,
      additionalFieldPlaceholder,
      additionalFieldMaxLength,
      additionalFieldOptionalText,
      additionalFieldLabel,
      foundryPlaceholder,
      isFoundryDisabled,
      computedFoundryOptions,
      computedFoundryValue,
      customType
    };
  }
}; 